import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Botao, Selo, type Estagio } from '../componentes/forma.tsx';
import { Lista } from '../componentes/painel.tsx';
import { api, ErroApi, query } from '../lib/api.ts';
import {
  dataBr, diasAtras, dinheiro, dinheiroBr, formatarCnpj, hojeIso,
} from '../lib/formato.ts';
import { usePode } from '../lib/sessao.tsx';
import './relatorios.css';

type Aba = 'exames' | 'faturamento';

/** O que existe DENTRO do período escolhido -- não o cadastro inteiro. */
interface Listas {
  empresas: { id: string; nome: string }[];
  medicos: { id: string; nome: string }[];
  convenios: { id: number; nome: string }[];
  /** O que a clínica chama de "tipo de exame": TÓRAX, COTOVELO, JOELHO. */
  procedimentos: { id: number; nome: string }[];
}

/* Produção médica e Pneumoconiose (OIT) saíram da tela por decisão da clínica.
   As rotas da API continuam de pé: quem quiser os números pede pela URL, e
   devolvê-las à tela é acrescentar duas linhas aqui. Apagar a apuração junto
   seria jogar fora o que ninguém pediu para jogar fora. */
const ABAS: { chave: Aba; rotulo: string; financeiro?: boolean }[] = [
  { chave: 'exames', rotulo: 'Exames' },
  { chave: 'faturamento', rotulo: 'Faturamento', financeiro: true },
];

export function Relatorios() {
  const pode = usePode();
  const verFinanceiro = pode('pode_financeiro');

  const [aba, setAba] = useState<Aba>('exames');
  const [de, setDe] = useState(() => diasAtras(30));
  const [ate, setAte] = useState(hojeIso);
  const [empresaId, setEmpresaId] = useState('');
  const [convenioId, setConvenioId] = useState('');
  const [medicoId, setMedicoId] = useState('');
  const [situacao, setSituacao] = useState('TODAS');
  const [procedimentoId, setProcedimentoId] = useState('');
  const [pagamento, setPagamento] = useState('TODOS');

  const consulta = query({
    de, ate, empresaId, convenioId, medicoId, procedimentoId, situacao, pagamento,
  });
  const visiveis = ABAS.filter((a) => !a.financeiro || verFinanceiro);

  /* As listas dos filtros saem do próprio período, e não do cadastro inteiro.
     A clínica tem dezenas de empresas; uma semana costuma ter três. Procurar
     entre dezenas para escolher uma das três é trabalho jogado fora -- e
     escolher qualquer uma das outras devolve um relatório vazio sem explicar
     por quê. */
  const filtros = useQuery({
    queryKey: ['relatorio', 'opcoes', aba, de, ate],
    queryFn: ({ signal }) =>
      api.get<Listas>(`/relatorios/opcoes${query({ de, ate, aba })}`, signal),
    placeholderData: (anterior) => anterior,
  });

  /* Se o que estava escolhido sumiu do período novo, a escolha cai junto.
     Sem isto o <select> voltaria a mostrar "Todas" -- porque o valor não existe
     mais entre as opções -- enquanto o filtro continuaria valendo por baixo: a
     tela diria uma coisa e o relatório mostraria outra. Um filtro invisível é
     pior do que um filtro que se perde. */
  const listas = filtros.data;
  useEffect(() => {
    if (!listas) return;
    const some = (lista: { id: string | number }[], v: string) =>
      v && !lista.some((x) => String(x.id) === v);
    if (some(listas.empresas, empresaId)) setEmpresaId('');
    if (some(listas.medicos, medicoId)) setMedicoId('');
    if (some(listas.convenios, convenioId)) setConvenioId('');
    if (some(listas.procedimentos, procedimentoId)) setProcedimentoId('');
  }, [listas, empresaId, medicoId, convenioId, procedimentoId]);

  const dados = useQuery({
    queryKey: ['relatorio', aba, consulta],
    queryFn: ({ signal }) => api.get<Record<string, never>>(`/relatorios/${aba}${consulta}`, signal),
    placeholderData: (anterior) => anterior,
  });

  const baixar = () => {
    window.open(`/api/relatorios/${aba}${consulta}&formato=csv`, '_blank');
  };

  const d = dados.data as Record<string, never> | undefined;
  const erro = dados.error instanceof ErroApi ? dados.error.message : undefined;

  return (
    <Layout>
      <Cabecalho titulo="Relatórios">
        <Botao variante="neutro" onClick={baixar} disabled={dados.isPending || !!erro}>
          Baixar em planilha
        </Botao>
      </Cabecalho>

      <div className="fila__filtros cartao">
        <nav className="abas" aria-label="Relatório">
          {visiveis.map((a) => (
            <button
              key={a.chave}
              className="aba"
              data-ativa={aba === a.chave || undefined}
              onClick={() => setAba(a.chave)}
            >
              {a.rotulo}
            </button>
          ))}
        </nav>

        <div className="rel__filtros">
          <label className="campo campo--terco">
            <span className="campo__rotulo rotulo">
              Data do exame
            </span>
            <span className="rel__periodo">
              <input className="entrada" type="date" value={de} onChange={(e) => setDe(e.target.value)} aria-label="De" />
              <span>a</span>
              <input className="entrada" type="date" value={ate} onChange={(e) => setAte(e.target.value)} aria-label="Até" />
            </span>
          </label>

          <label className="campo campo--terco">
            <span className="campo__rotulo rotulo">Empresa</span>
            <select className="entrada entrada--selecao" value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}>
              <option value="">Todas</option>
              {listas?.empresas.map((x) => (
                <option key={x.id} value={x.id}>{x.nome}</option>
              ))}
            </select>
          </label>

          <label className="campo campo--terco">
            <span className="campo__rotulo rotulo">Médico do laudo</span>
            <select className="entrada entrada--selecao" value={medicoId} onChange={(e) => setMedicoId(e.target.value)}>
              <option value="">Todos</option>
              {listas?.medicos.map((x) => (
                <option key={x.id} value={x.id}>{x.nome}</option>
              ))}
            </select>
          </label>

          {aba === 'exames' && (
            <>
              <label className="campo campo--terco">
                <span className="campo__rotulo rotulo">Convênio</span>
                <select className="entrada entrada--selecao" value={convenioId} onChange={(e) => setConvenioId(e.target.value)}>
                  <option value="">Todos</option>
                  {listas?.convenios.map((x) => (
                    <option key={x.id} value={x.id}>{x.nome}</option>
                  ))}
                </select>
              </label>
              <label className="campo campo--terco">
                <span className="campo__rotulo rotulo">Situação</span>
                <select className="entrada entrada--selecao" value={situacao} onChange={(e) => setSituacao(e.target.value)}>
                  <option value="TODAS">Todas</option>
                  <option value="PENDENTES">Pendentes</option>
                  <option value="FINALIZADO">Finalizados</option>
                  <option value="CANCELADO">Cancelados</option>
                </select>
              </label>
              {/* "Tipo de exame" no balcão é o procedimento -- TÓRAX, COTOVELO
                  --, e não admissional/periódico, que é o motivo do pedido. A
                  lista sai do período, como as outras: a clínica tem mais de
                  cem procedimentos cadastrados e uma semana usa um punhado. */}
              <label className="campo campo--terco">
                <span className="campo__rotulo rotulo">Exame</span>
                <select
                  className="entrada entrada--selecao"
                  value={procedimentoId}
                  onChange={(e) => setProcedimentoId(e.target.value)}
                >
                  <option value="">Todos</option>
                  {listas?.procedimentos.map((x) => (
                    <option key={x.id} value={x.id}>{x.nome}</option>
                  ))}
                </select>
              </label>
            </>
          )}

          {/* Só no faturamento. Na aba de exames ele saiu: quem abre aquela
              lista pergunta o que foi feito, não o que foi cobrado. */}
          {aba === 'faturamento' && verFinanceiro && (
            <label className="campo campo--terco">
              <span className="campo__rotulo rotulo">Pagamento</span>
              <select className="entrada entrada--selecao" value={pagamento} onChange={(e) => setPagamento(e.target.value)}>
                <option value="TODOS">Pagos e em aberto</option>
                <option value="ABERTO">Só em aberto</option>
                <option value="PAGO">Só pagos</option>
              </select>
            </label>
          )}
        </div>
      </div>

      {erro && <div className="cartao vazio"><p className="vazio__titulo">Não foi possível montar o relatório</p><p>{erro}</p></div>}

      {!erro && aba === 'exames' && <RelExames d={d} carregando={dados.isPending} />}
      {!erro && aba === 'faturamento' && <RelFaturamento d={d} carregando={dados.isPending} />}
    </Layout>
  );
}

/* ------------------------------------------------------------------ blocos */

function Numeros({ itens }: { itens: { rotulo: string; valor: string; destaque?: boolean }[] }) {
  return (
    <div className="rel__numeros">
      {itens.map((i) => (
        <div className="rel__numero cartao" key={i.rotulo} data-destaque={i.destaque || undefined}>
          <span className="rotulo">{i.rotulo}</span>
          <strong className="rel__valor num">{i.valor}</strong>
        </div>
      ))}
    </div>
  );
}

type Bloco = { d: Record<string, never> | undefined; carregando: boolean };

function RelExames({ d, carregando }: Bloco) {
  const itens = (d?.itens ?? []) as Record<string, string | number | boolean | null>[];
  const t = (d?.totais ?? {}) as Record<string, number>;
  const verValores = Boolean(d?.verValores);

  return (
    <>
      <Numeros
        itens={[
          { rotulo: 'Exames', valor: String(t.quantidade ?? 0) },
          { rotulo: 'Laudados', valor: String(t.laudados ?? 0) },
          ...(verValores
            ? [
                { rotulo: 'Valor dos exames', valor: dinheiroBr(t.valorExame), destaque: true },
                { rotulo: 'Valor dos laudos', valor: dinheiroBr(t.valorLaudo) },
              ]
            : []),
        ]}
      />
      <Lista
        carregando={carregando}
        vazio={itens.length === 0 ? { titulo: 'Nenhum exame no período', apoio: 'Amplie as datas ou revise os filtros.' } : undefined}
      >
        <table className="tabela">
          <thead>
            <tr>
              <th>Código</th>
              <th className="tabela--num">Data</th>
              <th>Paciente</th>
              <th>Exame</th>
              <th>Empresa / Convênio</th>
              <th>Situação</th>
              {verValores && <th className="tabela--num">Valor</th>}
            </tr>
          </thead>
          <tbody>
            {itens.map((e) => (
              <tr key={String(e.codigo)}>
                <td className="mono">{String(e.codigo)}</td>
                <td className="tabela--num mono">{dataBr(String(e.dataExame))}</td>
                <td className="tabela__nome">{String(e.paciente)}</td>
                <td className="fila__exame">{String(e.procedimento)}</td>
                <td className="fila__empresa">{String(e.empresa ?? e.convenio ?? '—')}</td>
                <td><Selo estagio={e.situacao as Estagio} /></td>
                {verValores && (
                  <td className="tabela--num mono">
                    {dinheiro(e.valorExame as number | null)}
                    {e.pago ? <span className="rel__pago"> pago</span> : null}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Lista>
    </>
  );
}

function RelFaturamento({ d, carregando }: Bloco) {
  const empresas = (d?.porEmpresa ?? []) as Record<string, string | number>[];
  const convenios = (d?.porConvenio ?? []) as Record<string, string | number>[];
  const t = (d?.totais ?? {}) as Record<string, number>;

  return (
    <>
      <Numeros
        itens={[
          { rotulo: 'Exames entregues', valor: String(t.quantidade ?? 0) },
          { rotulo: 'Valor total', valor: dinheiroBr(t.valor), destaque: true },
          { rotulo: 'Em aberto', valor: dinheiroBr(t.emAberto) },
        ]}
      />
      <p className="campo__dica" style={{ marginBottom: 'var(--e-3)' }}>
        Conta só exame já laudado ou entregue. Exame que ainda está na fila não entra no
        faturamento.
      </p>

      <div className="rel__duplo">
        <Lista carregando={carregando} vazio={empresas.length === 0 ? { titulo: 'Nada a faturar no período', apoio: 'Amplie as datas.' } : undefined}>
          <table className="tabela">
            <thead>
              <tr>
                <th>Empresa</th>
                <th className="tabela--num">Exames</th>
                <th className="tabela--num">Valor</th>
                <th className="tabela--num">Em aberto</th>
              </tr>
            </thead>
            <tbody>
              {empresas.map((e) => (
                <tr key={String(e.nome)}>
                  <td className="tabela__nome">
                    {String(e.nome)}
                    {e.cnpj && <span className="tabela__apoio"> · {formatarCnpj(String(e.cnpj))}</span>}
                  </td>
                  <td className="tabela--num num">{String(e.quantidade)}</td>
                  <td className="tabela--num mono">{dinheiro(e.valor as number)}</td>
                  <td className="tabela--num mono">{dinheiro(e.emAberto as number)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Lista>

        <Lista carregando={carregando} vazio={convenios.length === 0 ? { titulo: 'Sem convênios no período', apoio: '' } : undefined}>
          <table className="tabela">
            <thead>
              <tr>
                <th>Convênio</th>
                <th className="tabela--num">Exames</th>
                <th className="tabela--num">Valor</th>
              </tr>
            </thead>
            <tbody>
              {convenios.map((c) => (
                <tr key={String(c.nome)}>
                  <td className="tabela__nome">{String(c.nome)}</td>
                  <td className="tabela--num num">{String(c.quantidade)}</td>
                  <td className="tabela--num mono">{dinheiro(c.valor as number)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Lista>
      </div>
    </>
  );
}

